import * as React from 'react';

import KeyInformationCourseDurationItem from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationCourseDurationItem';
import KeyInformationCourseSatisfactionScore from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationCourseSatisfactionScore';
import KeyInformationDetailsItem from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationDetailsItem';
import KeyInformationDifficultyItem from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationDifficultyItem';
import KeyInformationEnterpriseCreditItem from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationEnterpriseCreditItem';
import KeyInformationFixedScheduleItem from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationFixedScheduleItem';
import KeyInformationHorizontalCourseDurationItem from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationHorizontalCourseDurationItem';
import KeyInformationHorizontalReviewsItem from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationHorizontalReviewsItem';
import KeyInformationModuleCount from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationModuleCount';
import KeyInformationReviewsItem from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationReviews';
import {
  MAX_NUMBER_OF_HORIZONTAL_KIM_ITEMS,
  MAX_NUMBER_OF_KIM_ITEMS,
} from 'bundles/unified-description-page-common/constants';
import type { EnterpriseCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseCoursePageQuery';
import { KeyInformationItemNames } from 'bundles/unified-description-page-common/types/moduleConfig';
import type { KeyInfoModule } from 'bundles/unified-description-page-common/types/moduleConfig';
import combineCourseDurationAndSchecule from 'bundles/unified-description-page-common/utils/combineCourseDurationAndSchecule';

import _t from 'i18n!nls/xdp';

const getEnterpriseCourseKeyInformationItems = (
  pageData: EnterpriseCoursePageFieldsFragment,
  keyInformationModuleConfig?: KeyInfoModule
) => {
  if (
    !keyInformationModuleConfig ||
    !keyInformationModuleConfig.items ||
    keyInformationModuleConfig.items.length === 0
  ) {
    return { items: [] };
  }

  let reviewItemIndex = -1;
  let hasCourseSatisfactionScoreItemConfig = false;

  let items = keyInformationModuleConfig.items.map((config, index) => {
    switch (config.name) {
      case KeyInformationItemNames.Reviews:
        if (pageData?.ratings?.averageFiveStarRating) {
          if (keyInformationModuleConfig.horizontalDisplay) {
            reviewItemIndex = index;
            return (
              <KeyInformationHorizontalReviewsItem
                productRating={pageData?.ratings?.averageFiveStarRating}
                totalReviewCount={pageData?.ratings?.ratingCount}
                key={config.name}
              />
            );
          }

          return (
            <KeyInformationReviewsItem
              productRating={pageData?.ratings?.averageFiveStarRating}
              totalReviewCount={pageData?.ratings?.ratingCount}
              averageContentSatisfactionScore={pageData?.averageContentSatisfactionScore}
              key={config.name}
            />
          );
        }
        return null;
      case KeyInformationItemNames.Difficulty:
        if (pageData.difficultyLevel) {
          return (
            <KeyInformationDifficultyItem
              difficultyLevel={pageData.difficultyLevel}
              cmlRecommendedBackground={pageData.cmlRecommendedBackground}
              numSubtitleLines={keyInformationModuleConfig.horizontalDisplay ? 2 : undefined}
              key={config.name}
            />
          );
        }
        return null;
      case KeyInformationItemNames.Duration:
        if (pageData.material?.totalDuration) {
          return keyInformationModuleConfig.horizontalDisplay ? (
            <KeyInformationHorizontalCourseDurationItem
              totalDuration={pageData.material?.totalDuration}
              key={config.name}
            />
          ) : (
            <KeyInformationCourseDurationItem totalDuration={pageData?.material?.totalDuration} key={config.name} />
          );
        }
        return null;
      case KeyInformationItemNames.Schedule:
        if (pageData.fixedSession) {
          return (
            <KeyInformationFixedScheduleItem
              fixedSession={pageData.fixedSession}
              isSelectedForCredit={!!pageData.isSelectedForCredit}
              // eslint-disable-next-line no-restricted-syntax
              productName={_t('course')}
            />
          );
        }
        return (
          <KeyInformationDetailsItem
            title={_t('Flexible schedule')}
            subtitle={_t('Learn at your own pace')}
            key={config.name}
          />
        );
      case KeyInformationItemNames.EnterpriseCredit:
        if (pageData.isSelectedForCredit) {
          return (
            <KeyInformationEnterpriseCreditItem
              fixedSession={pageData.fixedSession}
              isSelectedForCredit={!!pageData.isSelectedForCredit}
              // eslint-disable-next-line no-restricted-syntax
              productName={_t('course')}
            />
          );
        }
        return null;
      case KeyInformationItemNames.ModuleCount:
        return (
          <KeyInformationModuleCount
            moduleCount={pageData.material?.weeks.length ?? 0}
            key={KeyInformationItemNames.ModuleCount}
          />
        );
      case KeyInformationItemNames.CourseSatisfactionScore:
        hasCourseSatisfactionScoreItemConfig = true;
        if (!pageData.averageContentSatisfactionScore) return null;
        return (
          <KeyInformationCourseSatisfactionScore
            averageContentSatisfactionScore={pageData.averageContentSatisfactionScore}
            key={config.name}
          />
        );
      default:
        return <KeyInformationDetailsItem title={config.title} subtitle={config.subtitle} key={config.name} />;
    }
  });

  items = items.filter((item) => item !== null);

  if (!keyInformationModuleConfig.horizontalDisplay) {
    return { items };
  }

  // In showHorizontalOnXDP treatment variant
  if (hasCourseSatisfactionScoreItemConfig) {
    // Combine course duration and schedule items if exceeds MAX_NUMBER_OF_HORIZONTAL_KIM_ITEMS
    if (items.length > MAX_NUMBER_OF_HORIZONTAL_KIM_ITEMS) {
      items = combineCourseDurationAndSchecule({
        items,
        totalDuration: pageData.material?.totalDuration,
      });
    }
    // Thr horizontal KIM only supports a maximum of 5 items.
    return {
      items: items.slice(0, MAX_NUMBER_OF_HORIZONTAL_KIM_ITEMS),
      keyInformationIncludesReview: false,
    };
  }

  // The horizontal KIM only supports a maximum of 4 items.
  // If there are more than 4 items, we remove the reviews item from KIM.
  // Reviews will be displayed in the right side of the Hero module.
  if (items.length > MAX_NUMBER_OF_KIM_ITEMS && reviewItemIndex !== -1) {
    items.splice(reviewItemIndex, 1);

    return {
      items,
      keyInformationIncludesReview: false,
    };
  }

  return {
    items,
    keyInformationIncludesReview: reviewItemIndex !== -1,
  };
};

export default getEnterpriseCourseKeyInformationItems;
